import React, { useEffect, useState } from "react"
import { TableHead, TableBody, TableRow, Stack } from "@mui/material"
import { StyledTable, StyledHeaderCell, StyledFooterCell } from "../../../components/styled/StyledTableComponents"
import { COLORS } from "../../../utils"
import PackagingExecutionSection from "./PackagingExecutionSection"
import PECellValue from "../PECellValue"
import { convertKilosIntoGrams, convertGramsIntoKilos } from "../../../utils/ressources"
import { roundNumber } from "../../../utils"
import Typography from "@mui/material/Typography"

const PackagingExecutionSectionsForm = ({ sections = [], packagingForecastNumber, setFieldValue, setFieldTouched, editable = true, touchedSections = [], FCPackagingNumber = 1 }) => {

	const [focusedFieldIndex, setFocusedFieldIndex] = useState(null)

	const _renderKiloToGrams = (value) => {
		return roundNumber(convertKilosIntoGrams(value), 0)
	}

	/**
	 * calculate section realWeight (poids barquetté) = capacity (contenance) of each packaging
		*/
	const onChangeSectionRealWeight = (realWeight, index) => {
		if (realWeight === "") {
			setFieldValue(`sections[${index}].realWeight`, "")
			return
		}
		const realWeightKilos = convertGramsIntoKilos(realWeight)
		setFieldValue(`sections[${index}].realWeight`, realWeightKilos)

		const section = sections[index]
		const sectionProductionWeight = section.initialProductionWeight // already in kilos
		if (!sectionProductionWeight) return

		let newPackagingForecastNumber = Math.floor(sectionProductionWeight / (realWeightKilos * FCPackagingNumber))
		if (!isFinite(newPackagingForecastNumber)) { // if realWeight is 0 the division will return Infinity
			newPackagingForecastNumber = 0
		}
		setFieldValue(`sections[${index}].packagingForecastNumber`, newPackagingForecastNumber)
	}

	/** update waste by section on section changes */
	const _updateSectionsWaste = (sections, packagingForecastNumber, touchedSections) => {
		for (const section of sections) {
			const index = sections.indexOf(section)
			const sectionProductionWeight = section.initialProductionWeight
			const sectionRealWeight = section.realWeight
			if (!touchedSections?.[index]?.realWeight) {
				continue
			}
			if (!isFinite(sectionProductionWeight) || !isFinite(sectionRealWeight) || !isFinite(packagingForecastNumber)) {
				continue
			}
			const sectionForecastWaste = sectionProductionWeight - (packagingForecastNumber * sectionRealWeight * FCPackagingNumber)
			setFieldValue(`sections[${index}].forecastWaste`, sectionForecastWaste)
		}
	}

	useEffect(() => {
		_updateSectionsWaste(sections, packagingForecastNumber, touchedSections)
	}, [sections])

	// trigger all fields calculations from proposed weight if real weight has not yet been manually changed
	useEffect(() => {
		if (focusedFieldIndex === null) {
			for (const section of sections) {
				const index = sections.indexOf(section)
				if (!section.realWeight && isFinite(section.proposedWeight)) {
					setFieldTouched(`sections[${index}].realWeight`, true)
					const proposedWeightToGrams = convertKilosIntoGrams(section.proposedWeight)
					onChangeSectionRealWeight(proposedWeightToGrams, index)
				}
			}
		}
	}, [sections, focusedFieldIndex])

	//  update forecast packaging number with the minimum value of all sections' forecast packaging number
	useEffect(() => {
		const sectionsWithPackagingForecastNumber = sections.filter(section => section.packagingForecastNumber && isFinite(section.packagingForecastNumber))
		const packagingForecastNumbers = sectionsWithPackagingForecastNumber.map(section => section.packagingForecastNumber)
		if (packagingForecastNumbers.length > 0) {
			const minimumPackagingForecastNumber = Math.min(...packagingForecastNumbers)
			setFieldValue("packagingForecastNumber", minimumPackagingForecastNumber)
		}
	}, [sections])

	const headerLabels = ["Élements à barquetter", "Quantité produite / estimée", "Quantité de la fiche recette", "Quantité à barquetter", "Restes"]

	return (
		<Stack spacing={2} sx={{ width: "100%" }}>
			<Stack spacing={1} direction={"row"}>
				<img src="/img/packagingExecutions/distributionBySections.svg" alt="Packaging Icon" style={{ width: "24px", height: "24px", padding: "2px" }} />
				<Typography variant="h6" sx={{ fontSize: "16px", lineHeight: "24px", color: COLORS.BLACK800 }}>
					Répartition par sections
				</Typography>
			</Stack>
			{
				editable &&
				<Typography variant="p" sx={{ fontSize: "16px", lineHeight: "24px", color: "#7C7C7C" }}>
					Saisissez les quantités réelles des sections à barquetter
				</Typography>
			}

			<StyledTable>
				<TableHead>
					<TableRow>
						{headerLabels.map(label => (
							<StyledHeaderCell key={label}>{label}</StyledHeaderCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{sections.map((currentSection, index) => _renderKiloToGrams((currentSection.recipeSectionWeight || 0), 2) > 0 && (
						<PackagingExecutionSection
							key={index}
							editable={editable}
							section={currentSection}
							lastSectionIdx={sections.length - 1}
							index={index}
							setFieldTouched={(form) => form.setFieldTouched}
							renderKiloToGrams={_renderKiloToGrams}
							onChangeSectionRealWeight={(value) => onChangeSectionRealWeight(value, index)}
							setFocusedFieldIndex={setFocusedFieldIndex}
						/>
					))}
					<TableRow>
						<StyledFooterCell>
							Total
						</StyledFooterCell>
						<StyledFooterCell>
							<PECellValue
								value1={roundNumber(sections.reduce((acc, section) => acc + (section.productionWeight), 0), 2)}
								value2={roundNumber(sections.reduce((acc, section) => acc + (section.totalTheoreticalWeight), 0), 2)}
								unit="kg"
								style={{ color: COLORS.BLACK800 }}
							/>
						</StyledFooterCell>
						<StyledFooterCell>
							<PECellValue
								value1={_renderKiloToGrams(sections.reduce((acc, section) => acc + (section.recipeSectionWeight), 0))}
								unit="g"
								style={{ color: COLORS.BLACK800 }}
							/>
						</StyledFooterCell>
						<StyledFooterCell>
							<PECellValue
								value1={_renderKiloToGrams(sections.reduce((acc, section) => acc + (section.realWeight || 0), 0))}
								unit="g"
								style={{ color: COLORS.BLACK800 }}
							/>
						</StyledFooterCell>
						<StyledFooterCell disabled>
							<PECellValue
								value1={roundNumber(sections.reduce((acc, section) => acc + (section.forecastWaste || 0), 0), 1)}
								unit="kg"
								style={{ color: COLORS.BLACK800 }}
							/>
						</StyledFooterCell>
					</TableRow>
				</TableBody>
			</StyledTable>
		</Stack>)
}


export default PackagingExecutionSectionsForm
